import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { OVERLAY_Z, OverlayPortal } from "../utils/OverlayPortal";

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onClose: () => void;
}

/// Confirmation d'une action, au-dessus de tout le reste — étant toujours le dernier calque ouvert,
/// elle passe devant par simple ordre du document (cf. `OVERLAY_Z`).
///
/// Montée sous `<body>` : ouverte depuis un volet, elle héritait sinon du bloc conteneur du volet
/// (transform de l'animation d'ouverture) et s'affichait à la largeur de celui-ci au lieu de la
/// page entière. Voir `OverlayPortal`.
export function ConfirmModal({
  isOpen,
  title,
  message,
  onConfirm,
  onClose,
}: ConfirmModalProps) {
  const { t } = useTranslation();

  // `Shutter` ferme aussi sur Échap : la confirmation doit intercepter la touche en premier, sinon
  // valider ou annuler fermerait le volet en dessous.
  useEffect(() => {
    if (!isOpen) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onClose();
    };

    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <OverlayPortal>
      <div
        className={`fixed inset-0 ${OVERLAY_Z} flex select-none items-center justify-center p-6`}
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          aria-label={t("form.cancel")}
          onClick={onClose}
          className="absolute inset-0 bg-black/50"
        />

        <div className="relative w-full max-w-sm rounded-lg bg-white p-4 shadow-lg md:p-6 dark:bg-gray-800 dark:text-gray-100">
          <h2 className="text-lg font-bold dark:text-gray-100">{title}</h2>
          <p className="mt-2 dark:text-gray-200">{message}</p>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              {t("form.cancel")}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 dark:border-red-600 dark:bg-gray-800 dark:text-red-400 dark:hover:bg-red-950/40"
            >
              {t("form.validate")}
            </button>
          </div>
        </div>
      </div>
    </OverlayPortal>
  );
}
